import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, BarChart3, TrendingUp, Building2, Users, DollarSign,
  HardDrive, Folder, FileText, Download, RefreshCw, Activity, Eye
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, [activeTab]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const response = await axios.get(`${API_URL}/owner/reports/overview`);
        setOverviewData(response.data);
      } else if (activeTab === 'comparison') {
        const response = await axios.get(`${API_URL}/owner/reports/amc-comparison`);
        setComparisonData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatStorage = (mb) => {
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    }
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const exportToCSV = () => {
    if (!comparisonData) return;

    const headers = ['AMC Name', 'Status', 'Users', 'Funds', 'Investments', 'Total Invested', 'Current Value', 'ROI %', 'Storage (MB)', 'Files'];
    const rows = comparisonData.amcs.map(amc => [
      amc.amc_name,
      amc.is_active ? 'Active' : 'Inactive',
      amc.metrics.users,
      amc.metrics.funds,
      amc.metrics.investments,
      amc.metrics.total_invested,
      amc.metrics.current_value,
      amc.metrics.roi_percentage,
      amc.metrics.storage_mb,
      amc.metrics.files
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `amc-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/owner/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <BarChart3 className="mr-3 text-blue-600" size={32} />
                  Platform Reports & Analytics
                </h1>
                <p className="text-gray-600 mt-1">Comprehensive insights across all AMCs</p>
              </div>
            </div>
            <button
              onClick={fetchReports}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="inline mr-2" size={16} />
              Platform Overview
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'comparison'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="inline mr-2" size={16} />
              AMC Comparison
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading report data...</p>
          </div>
        ) : (
          <>
            {/* Platform Overview */}
            {activeTab === 'overview' && overviewData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Building2 className="text-blue-600" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">Total AMCs</p>
                    <p className="text-3xl font-bold text-gray-900">{overviewData.summary.total_amcs}</p>
                    <p className="mt-2 text-xs text-green-600">{overviewData.summary.active_amcs} Active</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Users className="text-green-600" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">Total Users</p>
                    <p className="text-3xl font-bold text-gray-900">{overviewData.summary.total_users}</p>
                    <p className="mt-2 text-xs text-gray-500">Across all AMCs</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <TrendingUp className="text-purple-600" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">Total Investments</p>
                    <p className="text-3xl font-bold text-gray-900">{overviewData.summary.total_investments}</p>
                    <p className="mt-2 text-xs text-gray-500">{overviewData.summary.total_funds} Funds</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-orange-100 rounded-lg">
                        <HardDrive className="text-orange-600" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">Total Storage</p>
                    <p className="text-3xl font-bold text-gray-900">{formatStorage(overviewData.summary.total_storage_mb)}</p>
                    <p className="mt-2 text-xs text-gray-500">{overviewData.summary.total_files} files</p>
                  </div>
                </div>

                {/* Financial Overview */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                    <DollarSign className="mr-2 text-gray-600" size={24} />
                    Financial Summary
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Total Invested Amount</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(overviewData.summary.total_invested_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Current Portfolio Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(overviewData.summary.total_current_value)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Total Returns</p>
                      <p className={`text-2xl font-bold ${
                        overviewData.summary.total_current_value >= overviewData.summary.total_invested_amount
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {formatCurrency(overviewData.summary.total_current_value - overviewData.summary.total_invested_amount)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Top AMCs */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                    <TrendingUp className="mr-2 text-gray-600" size={24} />
                    Top 5 AMCs by Investment Volume
                  </h3>
                  <div className="space-y-4">
                    {overviewData.top_amcs.map((amc, index) => (
                      <div key={amc.amc_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{amc.amc_name}</p>
                            <p className="text-sm text-gray-500">{amc.investment_count} investments</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatCurrency(amc.total_invested)}</p>
                          <button
                            onClick={() => navigate(`/owner/amcs/${amc.amc_id}`)}
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center mt-1"
                          >
                            <Eye size={14} className="mr-1" />
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* AMC Comparison */}
            {activeTab === 'comparison' && comparisonData && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">
                    Detailed AMC Comparison ({comparisonData.total_amcs} AMCs)
                  </h3>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            AMC Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Users
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Funds
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Investments
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Invested
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Value
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ROI %
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Storage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {comparisonData.amcs.map((amc) => (
                          <tr key={amc.amc_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{amc.amc_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                amc.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {amc.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {amc.metrics.users}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {amc.metrics.funds}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {amc.metrics.investments}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(amc.metrics.total_invested)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(amc.metrics.current_value)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`font-semibold ${
                                amc.metrics.roi_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {amc.metrics.roi_percentage}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>{formatStorage(amc.metrics.storage_mb)}</div>
                              <div className="text-xs text-gray-500">{amc.metrics.files} files</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => navigate(`/owner/amcs/${amc.amc_id}`)}
                                className="text-blue-600 hover:text-blue-700 flex items-center"
                              >
                                <Eye size={16} className="mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
